import { Box, Card, CardContent, Divider, FormControl, Grid, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import { useState } from "react";
import { useAppDispatch } from "../../../../app/hooks";
import { Localized } from "@fluent/react";
import LanguageIcon from "@mui/icons-material/Language";
import { CardActionButtons } from "./CardActionButtons";
import { PasswordConfirmationDialog } from "../dialogs/PasswordConfirmationDialog";
import { CompanySetting, restoreCompanySettings, updateCompanySettings } from "../companySettingsAPI";
import { showError, showSuccess } from "../../../notifications/notificationsSlice";
import { RestoreSettingsPasswordConfirmationDialog } from "../dialogs/RestoreSettingsPasswordConfirmationDialog";

interface CompanyUrsRequestMatchingSettingCardProps {
    companySettings: CompanySetting[];
    onUpdate: (updatedSettings: CompanySetting[]) => void;
}

export const CompanySettingsCard: React.FC<CompanyUrsRequestMatchingSettingCardProps> = ({ companySettings, onUpdate }) => {
    const dispatch = useAppDispatch();
    const [isEditing, setIsEditing] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState("");

    const orgLanguageSetting = companySettings.find(setting => setting.code === "DEFAULT_LANGUAGE");

    const [languageSetting, setLanguageSetting] = useState<CompanySetting | undefined>(orgLanguageSetting);

    const isDefault = languageSetting?.isDefault === true;

    const handleLanguageChange = (event) => {
        setLanguageSetting(prev => prev ? { ...prev, value: event.target.value } : undefined);
    };

    const handleRestore = () => {
        setRestoreDialogOpen(true);
    };

    const handleSave = () => {
        setDialogOpen(true);
    };

    const handleClose = () => {
        setDialogOpen(false);
        setServerError("")
    };

    const handleCloseRestore = () => {
        setRestoreDialogOpen(false);
        setServerError("")
    };

    const handleConfirmRestore = async (password: string) => {
        if (password) {
            setSaving(true)
            setServerError("")

            try {
                const result =
                    await dispatch(restoreCompanySettings({
                        password: password,
                        settings: ["DEFAULT_LANGUAGE"]
                    }))

                if (restoreCompanySettings.fulfilled.match(result)) {
                    const settings = result.payload as CompanySetting[];
                    onUpdate(settings);
                    setLanguageSetting(settings.find(f => f.code === "DEFAULT_LANGUAGE"));
                    setRestoreDialogOpen(false);
                    setIsEditing(false);
                    dispatch(showSuccess("saved"))
                }
                else {
                    let errorMsg = "error"

                    if (result.payload) {
                        if (result.payload.kind === 'http') {
                            const problem = result.payload.problem
                            if (problem) {
                                errorMsg = problem.title
                            }
                        }
                    }
                    setServerError(errorMsg)
                }
            }
            catch (error) {
                dispatch(showError("error"))
            }
            finally {
                setSaving(false)
            }
        }
    }

    const handleConfirmSave = async (password: string) => {
        if (password && languageSetting) {
            setSaving(true)
            setServerError("")

            try {
                const updatedSettings: CompanySetting[] = [languageSetting];

                const result =
                    await dispatch(updateCompanySettings({
                        password: password,
                        settings: updatedSettings
                    }))

                if (updateCompanySettings.fulfilled.match(result)) {
                    const settings = result.payload as CompanySetting[];
                    onUpdate(settings)
                    setLanguageSetting(settings.find(f => f.code === "DEFAULT_LANGUAGE"));
                    setDialogOpen(false);
                    setIsEditing(false);
                    dispatch(showSuccess("saved"))
                }
                else {
                    let errorMsg = "error"

                    if (result.payload) {
                        if (result.payload.kind === 'http') {
                            const problem = result.payload.problem
                            if (problem) {
                                errorMsg = problem.title
                            }
                        }
                    }
                    setServerError(errorMsg)
                }
            }
            catch (error) {
                dispatch(showError("error"))
            }
            finally {
                setSaving(false)
            }
        }
    }

    const handleCancel = () => {
        setIsEditing(false);
    };

    return (
        <Card sx={{ width: 500, borderRadius: 2, boxShadow: 3 }}>
            <CardContent>
                <Typography variant="h6" sx={{ marginBottom: 2 }}>
                    <Localized id="settings">_Ustawienia</Localized>
                </Typography>

                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center">
                            <LanguageIcon color="primary" sx={{ marginRight: 1 }} />
                            <FormControl fullWidth>
                                <InputLabel id="select-language-label"><Localized id="company-settings-default-language">_Język domyślny</Localized></InputLabel>
                                <Select
                                    labelId="select-language-label"
                                    value={languageSetting?.value || "EN"}
                                    onChange={handleLanguageChange}
                                    label={<Localized id="company-settings-default-language">_Język domyślny</Localized>}
                                    fullWidth
                                    disabled={!isEditing}
                                >
                                    <MenuItem value={'PL'}>
                                        <Localized id="polish">_Polski</Localized>
                                    </MenuItem>
                                    <MenuItem value={'EN'}>
                                        <Localized id="english">_Angielski</Localized>
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Grid>
                </Grid>
                
                <Divider sx={{ width: "100%", mt: 3 }} />
                <CardActionButtons
                    isDefault={isDefault}
                    isEditing={isEditing}
                    onRestore={handleRestore}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    onEdit={() => setIsEditing(true)}
                />
            </CardContent>
            {dialogOpen &&
                <PasswordConfirmationDialog
                    saving={saving}
                    serverError={serverError}
                    onClose={handleClose}
                    onConfirm={handleConfirmSave}
                />
            }
            {restoreDialogOpen &&
                <RestoreSettingsPasswordConfirmationDialog
                    saving={saving}
                    serverError={serverError}
                    onClose={handleCloseRestore}
                    onConfirm={handleConfirmRestore}
                />
            }
        </Card>
    );
};
